import React from 'react';
import { Link } from 'react-router-dom';
import { Radio, ArrowRight, ArrowUpRight, Users, Heart } from 'lucide-react';
import Newsletter from '../components/Newsletter';
import HeroVideo from '../components/HeroVideo';
import { studioImage, values, programmes, news } from '../mock';

const SectionEyebrow = ({ children }) => (
  <span className="dctv-eyebrow text-[#e8743b]">{children}</span>
);

const Hero = () => (
  <section className="relative overflow-hidden pt-[72px]">
    <HeroVideo />
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full pt-24 pb-28">
      <div className="max-w-3xl fade-up">
        <SectionEyebrow>Multicultural Community Media</SectionEyebrow>
        <h1 className="font-serif-dctv font-bold text-white leading-[1.05] mt-4 text-5xl md:text-7xl">
          Your story.<br />Your screen.<br /><span className="text-[#e8743b]">Your community.</span>
        </h1>
        <p className="text-[#bcc3cf] text-lg leading-relaxed mt-6 max-w-xl">
          Diaspora Community Television gives migrant and diaspora communities a voice online television and media built to promote social cohesion, dialogue and self-expression across the Netherlands, Europe, mainland China and our communities' countries of origin.
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-8">
          <Link to="/watch" className="dctv-btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[15px] font-semibold">
            <Radio className="w-4 h-4" /> Watch the channel
          </Link>
          <Link to="/about" className="dctv-btn-ghost inline-flex items-center px-7 py-3.5 rounded-full text-[15px] font-semibold">
            Who we are
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mt-8">
          {['The Hague · Europe', 'Mainland China', 'Countries of origin'].map((t) => (
            <span key={t} className="flex items-center gap-2 text-[#9aa3b2] text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#e8743b]" />{t}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const OnAir = () => (
  <section className="py-24 px-6 lg:px-10 bg-[#0c1119]">
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#e8743b]/12 border border-[#e8743b]/25">
          <span className="w-2 h-2 rounded-full bg-[#e8743b] pulse-dot" />
          <span className="text-[#e8743b] text-xs font-semibold tracking-wider">ON AIR</span>
        </span>
        <h2 className="font-serif-dctv text-4xl md:text-5xl font-bold text-white mt-6 leading-tight">Community broadcasts, streaming online</h2>
        <p className="text-[#9aa3b2] text-[15px] leading-relaxed mt-5 max-w-md">
          News, culture and conversation from the diaspora streamed online so anyone, anywhere can take part.
        </p>
        <Link to="/watch" className="inline-flex items-center gap-2 mt-8 text-[#e8743b] font-semibold text-[15px] group">
          Watch now <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
      <div className="relative">
        <div className="relative rounded-2xl overflow-hidden border border-white/8 shadow-2xl">
          <img src={studioImage} alt="Community television presenter in the studio" className="w-full h-[420px] object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,14,22,0) 60%, rgba(10,14,22,0.7) 100%)' }} />
          <span className="absolute bottom-4 left-4 text-white/90 text-sm font-medium">Global community reach</span>
        </div>
      </div>
    </div>
  </section>
);

const WhoWeAre = () => (
  <section className="py-24 px-6 lg:px-10">
    <div className="max-w-[1400px] mx-auto">
      <div className="max-w-3xl">
        <SectionEyebrow>Who we are</SectionEyebrow>
        <h2 className="font-serif-dctv text-4xl md:text-5xl font-bold text-white mt-5">Media that belongs to the community</h2>
        <p className="text-[#9aa3b2] text-[15px] leading-relaxed mt-6">
          Diaspora Community Television (DCTV) is a multicultural media platform with a mandate to promote social cohesion, community dialogue, voice and self-expression through online television and other electronic media. DCTV facilitates thematic debates and forums, creates access to information and knowledge-sharing, and builds the visibility and inclusion of diaspora communities in public decision-making through media. Our outreach is widespread and global, including the Netherlands, the United Kingdom, major European cities, mainland China and the diaspora's respective countries of origin.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
        {values.map((v) => (
          <div key={v.title} className="dctv-card rounded-2xl p-7">
            <h3 className="font-serif-dctv text-2xl font-semibold text-[#e2b25f]">{v.title}</h3>
            <p className="text-[#9aa3b2] text-sm leading-relaxed mt-3">{v.text}</p>
          </div>
        ))}
      </div>
      <div className="mt-12">
        <Link to="/about" className="dctv-btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold">
          Read our full story <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

const ProgrammesSection = () => (
  <section className="py-24 px-6 lg:px-10 bg-[#0c1119]">
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-xl">
          <SectionEyebrow>What we make</SectionEyebrow>
          <h2 className="font-serif-dctv text-4xl md:text-5xl font-bold text-white mt-5">Programmes &amp; themes</h2>
          <p className="text-[#9aa3b2] text-[15px] leading-relaxed mt-5">
            Thematic debates, forums and stories that build understanding and inclusion across the global village.
          </p>
        </div>
        <Link to="/programmes" className="dctv-btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap">
          All programmes <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
        {programmes.slice(0, 8).map((p) => (
          <Link to="/programmes" key={p.num} className="dctv-card rounded-2xl p-7 group">
            <span className="font-serif-dctv text-2xl text-[#e8743b] font-semibold">{p.num}</span>
            <h3 className="font-serif-dctv text-xl font-semibold text-white mt-3 group-hover:text-[#f4965a] transition-colors">{p.title}</h3>
            <p className="text-[#9aa3b2] text-[13.5px] leading-relaxed mt-3">{p.text}</p>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const NewsSection = () => {
  const featured = news.find((n) => n.featured);
  const rest = news.filter((n) => !n.featured);
  return (
    <section className="py-24 px-6 lg:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <SectionEyebrow>News &amp; blog</SectionEyebrow>
            <h2 className="font-serif-dctv text-4xl md:text-5xl font-bold text-white mt-5">Latest from the community</h2>
            <p className="text-[#9aa3b2] text-[15px] leading-relaxed mt-5">
              Reporting on the issues that shape diaspora life - migration and integration policy in the Netherlands, China's evolving immigration rules, and diaspora affairs worldwide.
            </p>
          </div>
          <Link to="/news" className="dctv-btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap">
            All news <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-14">
          <Link to={`/news/${featured.id}`} className="dctv-card rounded-2xl overflow-hidden group">
            <div className="relative h-72 overflow-hidden">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="p-7">
              <span className="dctv-eyebrow text-[#e8743b]">Featured · {featured.category}</span>
              <p className="text-[#6b7384] text-xs mt-2">{featured.date}</p>
              <h3 className="font-serif-dctv text-2xl font-bold text-white mt-3 leading-snug">{featured.title}</h3>
              <p className="text-[#9aa3b2] text-sm leading-relaxed mt-3">{featured.excerpt}</p>
              <div className="flex items-center gap-3 mt-5">
                <img src={featured.author.avatar} alt={featured.author.name} className="w-9 h-9 rounded-full object-cover object-top border border-white/10" />
                <div className="leading-tight">
                  <p className="text-white text-sm font-medium">{featured.author.name}</p>
                  <p className="text-[#6b7384] text-xs">{featured.author.role}</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-2 mt-5 text-[#e8743b] font-semibold text-sm">Read story <ArrowRight className="w-4 h-4" /></span>
            </div>
          </Link>

          <div className="flex flex-col gap-6">
            {rest.map((n) => (
              <Link to={`/news/${n.id}`} key={n.id} className="dctv-card rounded-2xl p-5 flex gap-5 group">
                <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-[#0a0e16] flex items-center justify-center">
                  {n.image ? (
                    <img src={n.image} alt={n.title} className="w-full h-full object-cover" />
                  ) : (
                    <Radio className="w-8 h-8 text-[#e8743b]" />
                  )}
                </div>
                <div className="flex-1">
                  <span className="dctv-eyebrow text-[#e8743b] text-[10px]">{n.category}</span>
                  <p className="text-[#6b7384] text-xs mt-1">{n.date}</p>
                  <h4 className="font-serif-dctv text-[17px] font-semibold text-white mt-1.5 leading-snug group-hover:text-[#f4965a] transition-colors">{n.title}</h4>
                  <div className="flex items-center gap-2 mt-2.5">
                    <img src={n.author.avatar} alt={n.author.name} className="w-6 h-6 rounded-full object-cover object-top border border-white/10" />
                    <span className="text-[#9aa3b2] text-xs">{n.author.name} · <span className="text-[#6b7384]">{n.author.role}</span></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const InvolveCta = () => (
  <section className="px-6 lg:px-10 pb-8">
    <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-6">
      <div className="dctv-card rounded-2xl p-9">
        <Users className="w-7 h-7 text-[#e8743b]" />
        <h3 className="font-serif-dctv text-2xl font-bold text-white mt-4">Volunteer &amp; internships</h3>
        <p className="text-[#9aa3b2] text-sm leading-relaxed mt-3">DCTV depends on the strength of its volunteers and interns to run programmes. Join us and showcase your creativity.</p>
        <Link to="/get-involved" className="dctv-btn-primary inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold mt-6">Volunteer now</Link>
      </div>
      <div className="dctv-card rounded-2xl p-9">
        <Heart className="w-7 h-7 text-[#e8743b]" />
        <h3 className="font-serif-dctv text-2xl font-bold text-white mt-4">Support our work</h3>
        <p className="text-[#9aa3b2] text-sm leading-relaxed mt-3">With your support and donation, we can offer community members the tools and environment to share their unique voice and creativity.</p>
        <Link to="/get-involved" className="dctv-btn-primary inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold mt-6">Donate</Link>
      </div>
    </div>
  </section>
);

const Home = () => (
  <div>
    <Hero />
    <OnAir />
    <WhoWeAre />
    <ProgrammesSection />
    <NewsSection />
    <InvolveCta />
    <Newsletter />
  </div>
);

export default Home;
